import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { PAGINATION_DEFAULT_SIZE } from '@src/core/config';
import type { DroneEntity } from '@src/core/features';
import { DRONE_ID_QUERY_KEY } from '../constants';
import { selectDrone, setSelectedDrone } from '../redux';

interface UseFleetDroneUrlSyncOptions {
  allDrones: DroneEntity[];
  displayDrones: DroneEntity[];
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

/**
 * Persists the selected fleet drone in `?droneId=` and restores it on reload.
 * List page is derived from the drone's index in the full list.
 */
export function useFleetDroneUrlSync({
  allDrones,
  displayDrones,
  isLoading,
  page,
  onPageChange,
}: UseFleetDroneUrlSyncOptions) {
  const dispatch = useDispatch();
  const selectedDrone = useSelector(selectDrone);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasRestoredRef = useRef(false);
  /** After URL restore, wait until pagination lands on this page before auto-select may run. */
  const pendingRestorePageRef = useRef<number | null>(null);

  // Restore selection from URL once drones are available.
  useEffect(() => {
    if (hasRestoredRef.current || isLoading || allDrones.length === 0) return;
    hasRestoredRef.current = true;

    const rawId = searchParams.get(DRONE_ID_QUERY_KEY);
    if (!rawId) return;

    const droneId = Number(rawId);
    if (!Number.isFinite(droneId)) return;

    const index = allDrones.findIndex((drone) => drone.id === droneId);
    if (index < 0) return;

    const targetPage = Math.floor(index / PAGINATION_DEFAULT_SIZE);
    pendingRestorePageRef.current = targetPage;
    onPageChange(targetPage);
    dispatch(setSelectedDrone(allDrones[index]));
  }, [allDrones, dispatch, isLoading, onPageChange, searchParams]);

  // Auto-select first visible drone when nothing is selected / selection left the page.
  useEffect(() => {
    if (!hasRestoredRef.current) return;

    if (pendingRestorePageRef.current != null) {
      if (page !== pendingRestorePageRef.current) return;
      pendingRestorePageRef.current = null;
    }

    const inList = displayDrones.some((drone) => drone.id === selectedDrone?.id);
    if (displayDrones.length > 0 && (!selectedDrone || !inList)) {
      dispatch(setSelectedDrone(displayDrones[0]));
    }
    if (displayDrones.length === 0 && selectedDrone) {
      dispatch(setSelectedDrone(null));
    }
  }, [displayDrones, page, selectedDrone, dispatch]);

  // Keep URL in sync with the current selection.
  useEffect(() => {
    if (!hasRestoredRef.current) return;

    const current = searchParams.get(DRONE_ID_QUERY_KEY);
    const nextId = selectedDrone?.id != null ? String(selectedDrone.id) : null;

    if (current === nextId) return;

    const next = new URLSearchParams(searchParams);
    if (nextId) next.set(DRONE_ID_QUERY_KEY, nextId);
    else next.delete(DRONE_ID_QUERY_KEY);
    setSearchParams(next, { replace: true });
  }, [searchParams, selectedDrone?.id, setSearchParams]);

  return { selectedDrone };
}
